import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText, ArrowRight } from 'lucide-react';

const Section = ({ title, children }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mb-10"
    >
        <h2 className="font-serif text-xl text-white mb-4 pb-3" style={{ borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
            {title}
        </h2>
        <div className="text-gray-400 text-sm leading-relaxed space-y-3">
            {children}
        </div>
    </motion.div>
);

const TermsPage = () => {
    return (
        <div className="font-sans bg-[#0e0e0e] min-h-screen text-white">

            {/* ── Nav bar ── */}
            <div
                className="fixed top-0 left-0 w-full z-50"
                style={{ background: 'rgba(14,14,14,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(212,175,55,0.12)' }}
            >
                {/* Desktop */}
                <div className="hidden md:flex items-center justify-between px-8 py-4">
                    <Link to="/" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-xs uppercase tracking-widest">
                        <ArrowLeft size={15} /> Home
                    </Link>
                    <Link to="/" className="flex items-center gap-2 leading-none">
                        <span className="font-serif text-base text-white tracking-widest">ROYAL</span>
                        <img src="/logo.png" alt="Royal Standard Logo" style={{ height: '28px', width: 'auto', objectFit: 'contain' }} />
                        <span className="font-serif text-base tracking-widest" style={{ color: '#D4AF37' }}>STANDARD</span>
                    </Link>
                    <Link to="/contact" className="text-xs uppercase tracking-widest px-5 py-2.5 font-semibold text-black hover:opacity-90" style={{ background: '#D4AF37' }}>
                        Get a Quote
                    </Link>
                </div>
                {/* Mobile */}
                <div className="md:hidden">
                    <div className="flex items-center justify-between px-4 py-3">
                        <Link to="/" className="flex items-center gap-1.5 text-white/70 hover:text-white transition-colors text-xs uppercase tracking-wider">
                            <ArrowLeft size={14} />
                        </Link>
                        <Link to="/" className="flex items-center gap-1.5 leading-none">
                            <span className="font-serif text-sm text-white tracking-widest">ROYAL</span>
                            <img src="/logo.png" alt="Royal Standard Logo" style={{ height: '22px', width: 'auto', objectFit: 'contain' }} />
                            <span className="font-serif text-sm tracking-widest" style={{ color: '#D4AF37' }}>STANDARD</span>
                        </Link>
                        <div className="w-6" />
                    </div>
                    <Link to="/contact" className="flex items-center justify-center gap-2 w-full py-2.5 text-black text-xs uppercase tracking-widest font-semibold" style={{ background: '#D4AF37' }}>
                        Get a Free Quote <ArrowRight size={12} />
                    </Link>
                </div>
            </div>

            {/* ── Hero ── */}
            <section className="pt-32 pb-16 px-5 md:px-6 text-center" style={{ borderBottom: '1px solid rgba(212,175,55,0.1)' }}>
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-6"
                    style={{ background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.25)' }}
                >
                    <FileText size={26} style={{ color: '#D4AF37' }} />
                </motion.div>
                <motion.p
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    className="uppercase tracking-[0.3em] text-xs font-semibold mb-4"
                    style={{ color: '#D4AF37' }}
                >
                    Legal
                </motion.p>
                <motion.h1
                    initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                    className="font-serif text-4xl md:text-6xl text-white mb-5 leading-tight"
                >
                    Terms & <em className="not-italic" style={{ color: '#D4AF37' }}>Conditions</em>
                </motion.h1>
                <motion.p
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                    className="text-gray-500 text-sm max-w-xl mx-auto"
                >
                    Last updated: February 2025 &nbsp;·&nbsp; Royal Standard LLC, Dubai, UAE
                </motion.p>
            </section>

            {/* ── Content ── */}
            <div className="max-w-3xl mx-auto px-5 md:px-6 py-16">

                <Section title="1. Agreement to Terms">
                    <p>
                        By accessing or using the website <strong className="text-white">royalstandarduae.com</strong> or engaging
                        Royal Standard for any services, you agree to be bound by these Terms and Conditions.
                        If you disagree with any part of these terms, please do not use our website or services.
                    </p>
                </Section>

                <Section title="2. Our Services">
                    <p>Royal Standard provides professional home finishing and interior services including, but not limited to:</p>
                    <ul className="list-disc list-inside space-y-1 pl-2">
                        <li>Marble polishing and restoration</li>
                        <li>Tile and marble installation</li>
                        <li>Ceiling works (gypsum, cove, coffered)</li>
                        <li>Joinery and custom carpentry</li>
                    </ul>
                    <p>
                        All services are subject to a formal quotation and agreement before commencement. Service availability
                        may vary by location within the UAE.
                    </p>
                </Section>

                <Section title="3. Quotations & Pricing">
                    <p>
                        All quotations are provided free of charge and are valid for 30 days from the date of issue unless otherwise stated.
                        Quoted prices are fixed and inclusive of labour and standard materials unless variations are requested by the client after agreement.
                    </p>
                    <p>
                        Additional costs may arise if unforeseen structural issues, material unavailability, or scope changes occur.
                        Any such changes will be communicated and agreed upon in writing before proceeding.
                    </p>
                </Section>

                <Section title="4. Payment Terms">
                    <p>Unless otherwise agreed in writing:</p>
                    <ul className="list-disc list-inside space-y-1 pl-2">
                        <li>A deposit of up to 50% may be required before work commences</li>
                        <li>The balance is due upon completion of the agreed scope of work</li>
                        <li>Accepted payment methods include bank transfer and cash (details provided in your quotation)</li>
                    </ul>
                    <p>Late payments may incur a delay in scheduling subsequent work or additional charges as outlined in your service agreement.</p>
                </Section>

                <Section title="5. Client Responsibilities">
                    <p>You agree to:</p>
                    <ul className="list-disc list-inside space-y-1 pl-2">
                        <li>Provide accurate information about the property and scope of work</li>
                        <li>Ensure reasonable access to the property at agreed times</li>
                        <li>Inform us of any building regulations, HOA rules, or landlord restrictions that may affect the work</li>
                        <li>Keep the site reasonably clear and safe for our team</li>
                    </ul>
                </Section>

                <Section title="6. Warranty & Workmanship Guarantee">
                    <p>
                        Royal Standard guarantees all workmanship for a period of <strong className="text-white">12 months</strong> from
                        the date of project completion. This warranty covers defects directly attributable to our workmanship.
                    </p>
                    <p>
                        The warranty does not cover: damage caused by misuse, accidental damage, natural wear and tear, third-party
                        modifications, or conditions arising from structural defects outside our scope.
                    </p>
                </Section>

                <Section title="7. Cancellation & Rescheduling">
                    <p>
                        Cancellations made more than 48 hours before the scheduled start date will incur no penalty.
                        Cancellations within 48 hours may result in forfeiture of any deposit paid to cover mobilisation costs.
                    </p>
                    <p>
                        We reserve the right to reschedule in exceptional circumstances (extreme weather, emergency situations).
                        Affected clients will be notified as soon as possible and rescheduled at the earliest available date.
                    </p>
                </Section>

                <Section title="8. Limitation of Liability">
                    <p>
                        To the maximum extent permitted by UAE law, Royal Standard shall not be liable for any indirect, incidental,
                        or consequential damages arising from the use of our services or website. Our total liability to you shall
                        not exceed the total amount paid for the specific service giving rise to the claim.
                    </p>
                </Section>

                <Section title="9. Intellectual Property">
                    <p>
                        All content on this website — including images, text, logos, and design — is the property of Royal Standard
                        and is protected by applicable intellectual property laws. You may not reproduce, distribute, or use any
                        content without our prior written consent.
                    </p>
                </Section>

                <Section title="10. Governing Law">
                    <p>
                        These Terms and Conditions are governed by the laws of the United Arab Emirates. Any disputes arising
                        shall be subject to the exclusive jurisdiction of the courts of Dubai, UAE.
                    </p>
                </Section>

                <Section title="11. Changes to Terms">
                    <p>
                        We reserve the right to modify these Terms and Conditions at any time. Updated terms will be posted on
                        this page with a revised date. Continued use of our website or services constitutes acceptance of the
                        revised terms.
                    </p>
                </Section>

                <Section title="12. Contact Us">
                    <p>For questions about these Terms and Conditions, please contact us:</p>
                    <ul className="list-disc list-inside space-y-1 pl-2">
                        <li>Email: <a href="mailto:info@royalstandarduae.com" className="text-yellow-400 hover:underline">info@royalstandarduae.com</a></li>
                        <li>Phone: <a href="tel:+971581735200" className="text-yellow-400 hover:underline">+971 58 173 5200</a></li>
                        <li>Location: Dubai, United Arab Emirates</li>
                    </ul>
                </Section>

                {/* CTA */}
                <div className="mt-12 text-center">
                    <Link
                        to="/privacy"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-gray-500 hover:text-yellow-400 transition-colors"
                    >
                        Read our Privacy Policy <ArrowRight size={13} />
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default TermsPage;
